import { Host, LocationCoupon, LocationImage } from "../models/hostModel";
import { PoorConditionsReport } from "../models/poorConditionsReportModel";
import { readFileAsArrayBuffer } from "../Utils";
import { backendRequest } from "./backendApi";
import exceptionService from "./exceptionService";
const call = async <T = any>(path: string, body?: any) => {
  const r = await backendRequest<T>(path, body);
  if (r.error) exceptionService.logError(r.error, `LocationsService:${path}`);
  return r;
};
const bool = async (path: string, body: any) => {
  const r = await call<boolean>(path, body);
  return r.data === true;
};
const settings = (h: Host) => ({
  name: h.name,
  address: h.address,
  city: h.city,
  state: h.state,
  zip: h.zip,
  phone: h.phone,
  latitude: h.latitude,
  longitude: h.longitude,
  timeAllowed: h.timeAllowed,
  doorLockCode: h.doorLockCode,
  useQRCode: h.useQRCode,
  closedForCleaning: h.closedForCleaning,
  placeId: h.placeId,
  offerFamilyRestroom: h.offerFamilyRestroom,
  offerMenBabyChaningTable: h.offerMenBabyChaningTable,
  offerWomenBabyChaningTable: h.offerWomenBabyChaningTable,
  offerSeatCovers: h.offerSeatCovers,
  offerWetWipes: h.offerWetWipes,
  offerHandicapAccessible: h.offerHandicapAccessible,
  offerPetFriendly: h.offerPetFriendly,
  isBusiness: h.isBusiness,
  hostPremiumOnly: h.hostPremiumOnly,
  doorLockCodeRequired: h.doorLockCodeRequired,
  offerHandSanitizer: h.offerHandSanitizer,
  localRep: h.localRep,
  offerWomenProducts: h.offerWomenProducts,
  offerMenProducts: h.offerMenProducts,
  motorcycleFriendly: h.motorcycleFriendly,
  overnightFriendly: h.overnightFriendly,
  offerAutoCharger: h.offerAutoCharger,
});
const locationsService = {
  async fetchAllLocationsForUser(_userId?: string, _email?: string): Promise<Host[]> {
    return (await call<Host[]>("/locations/list")).data ?? [];
  },
  async getHostRecord(id: number): Promise<Host | null> {
    return (await call<Host>("/locations/get", { id })).data ?? null;
  },
  async fetchLocationCurrentQue(hostId?: number): Promise<number> {
    if (hostId == null) return -1;
    return (await call<number>("/locations/queue", { hostId })).data ?? -1;
  },
  async fetchLocationPoorConditionReports(hostId?: number) {
    return call("/locations/reports", { hostId });
  },
  async fetchLocationActivity(hostId?: string) {
    return call("/locations/activity", { hostId });
  },
  async fetchLocationActivityById(id?: string) {
    return call("/locations/activity-by-id", { id });
  },
  async fetchLocationImages(hostId: number): Promise<LocationImage[]> {
    return (await call<LocationImage[]>("/locations/images", { hostId })).data ?? [];
  },
  async uploadLocationImage(hostId: number, file: File): Promise<LocationImage | null> {
    try {
      const contentType = file.type || "image/jpeg";
      const r = await backendRequest<LocationImage>(
        `/locations/images-upload?hostId=${hostId}&contentType=${encodeURIComponent(contentType)}`,
        (await readFileAsArrayBuffer(file)) as ArrayBuffer
      );
      if (r.error) exceptionService.logError(r.error, "LocationsService:uploadLocationImage");
      return r.error ? null : r.data;
    } catch (error) {
      exceptionService.logError(error, "LocationsService:uploadLocationImage");
      return null;
    }
  },
  async setPrimaryLocationImage(hostId: number, imageId: number): Promise<boolean> {
    return bool("/locations/images-primary", { hostId, imageId });
  },
  async deleteLocationImage(hostId: number, imageId: number): Promise<LocationImage[] | null> {
    const r = await call<LocationImage[]>("/locations/images-delete", { hostId, imageId });
    return r.error ? null : r.data ?? [];
  },
  async fetchLocationCoupons(hostId: number): Promise<LocationCoupon[]> {
    return (await call<LocationCoupon[]>("/locations/coupons", { hostId })).data ?? [];
  },
  async uploadLocationCoupon(hostId: number, file: File): Promise<LocationCoupon | null> {
    try {
      const contentType = file.type || "image/jpeg";
      const r = await backendRequest<LocationCoupon>(
        `/locations/coupons-upload?hostId=${hostId}&contentType=${encodeURIComponent(contentType)}`,
        (await readFileAsArrayBuffer(file)) as ArrayBuffer
      );
      if (r.error) exceptionService.logError(r.error, "LocationsService:uploadLocationCoupon");
      return r.error ? null : r.data;
    } catch (error) {
      exceptionService.logError(error, "LocationsService:uploadLocationCoupon");
      return null;
    }
  },
  async deleteLocationCoupon(hostId: number, couponId: number): Promise<LocationCoupon[] | null> {
    const r = await call<LocationCoupon[]>("/locations/coupons-delete", { hostId, couponId });
    return r.error ? null : r.data ?? [];
  },
  async doesLocationExistAtAddress(
    address: string,
    city: string,
    state: string,
    zip: string
  ): Promise<boolean> {
    return (await call<boolean>("/locations/exists", { address, city, state, zip })).data === true;
  },
  async insertLocation(host: Host, _userId: string): Promise<Boolean> {
    return bool("/locations/insert", { host });
  },
  async clearLocationFailedPayments(hostId: string): Promise<Boolean> {
    return bool("/locations/clear-failed", { hostId });
  },
  async getLocationsCount(): Promise<number> {
    return (await call<number>("/locations/count")).data ?? -1;
  },
  async updateLocationSettingsObject(host: Host): Promise<Boolean> {
    return bool("/locations/update-settings", { id: host.id, values: settings(host) });
  },
  async updateLocationHours(host: Host): Promise<Boolean> {
    return bool("/locations/update-hours", { id: host.id, hours: host.hours });
  },
  async updateLocationIs24Hours(hostId: number, is24Hours: boolean): Promise<Boolean> {
    return bool("/locations/update-24-hours", { id: hostId, is24Hours });
  },
  async updateLocationHoursAnd24Hours(host: Host): Promise<Boolean> {
    return bool("/locations/update-hours-24", {
      id: host.id,
      hours: host.hours,
      is24Hours: host.is24Hours,
    });
  },
  async updateOpenCloseLocation(id: number, closedForCleaning: boolean): Promise<Boolean> {
    return bool("/locations/update-open", { id, closedForCleaning });
  },
  async removeLocation(id: number, cancellationDate: Date): Promise<Boolean> {
    return bool("/locations/remove", { id, cancellationDate });
  },
  async insertPoorConditionReport(report: PoorConditionsReport): Promise<boolean> {
    let pictureUrl: any = report.picture;
    if (report.picture) {
      const source = (report.picture as any).uri ?? report.picture;
      const blob = await (await fetch(source)).blob();
      const upload = await backendRequest<string>(
        `/locations/report-upload?workflowId=${report.workflowId}`,
        (await readFileAsArrayBuffer(blob)) as ArrayBuffer
      );
      if (upload.error) return false;
      pictureUrl = upload.data;
    }
    return bool("/locations/report-insert", { report, pictureUrl });
  },
};
export default locationsService;
